import type { Client } from '../../../api/client.js';

/**
 * Actes notariés du dossier (PARITE-4, réunion du 16/10 #33) : lectures et écritures par les seules routes du contrat
 * (`actesNotaries.*`) ; le fichier d'un acte est une pièce de la GED existante (`documents.lister`), jamais une seconde copie.
 */
export type StatutActe = 'a_preparer' | 'en_cours' | 'signe' | 'publie';

export interface ActeNotarie {
  id: string;
  dossierId: string;
  successionId: string | null;
  deceasedDisplayName: string | null;
  kind: string;
  kindLabel: string;
  status: StatutActe;
  actDate: string | null;
  etude: string | null;
  reference: string | null;
  documentId: string | null;
  documentTitle: string | null;
  note: string | null;
  createdAt: string;
  createdByDisplayName: string;
  rowVersion: number;
}

export interface DonneesActes {
  types: { code: string; label: string }[];
  successions: { id: string; deceasedDisplayName: string }[];
  actes: ActeNotarie[];
}

export interface NouvelActe {
  kind: string;
  status: StatutActe;
  successionId?: string;
  actDate?: string;
  etude?: string;
  reference?: string;
  documentId?: string;
  note?: string;
}

export interface PieceGed {
  id: string;
  title: string;
}

export interface SourceActesNotaries {
  lister(): Promise<DonneesActes>;
  /** Pièces de la GED du dossier, pour y rattacher le fichier de l'acte. */
  pieces(): Promise<PieceGed[]>;
  creer(e: NouvelActe): Promise<ActeNotarie>;
  modifier(id: string, modification: Partial<NouvelActe>, versionLue: number): Promise<ActeNotarie>;
  retirer(id: string): Promise<void>;
}

export const PERMISSION_ECRIRE_ACTES = 'acte:write:dossier';

export function creerSourceActesNotaries(client: Client, dossierId: string): SourceActesNotaries {
  return {
    lister: () => client.appeler<DonneesActes>('actesNotaries.lister', { parametres: { dossierId } }),
    async pieces() {
      const documents = await client.appeler<{ id: string; title: string }[]>('documents.lister', { parametres: { dossierId } });
      return Array.isArray(documents) ? documents.map((d) => ({ id: d.id, title: d.title })) : [];
    },
    creer: (e) => client.appeler<ActeNotarie>('actesNotaries.creer', { parametres: { dossierId }, corps: e }),
    modifier: (id, modification, versionLue) =>
      client.appeler<ActeNotarie>('actesNotaries.modifier', { parametres: { acteId: id }, corps: modification, versionLigne: versionLue }),
    retirer: async (id) => {
      await client.appeler('actesNotaries.retirer', { parametres: { acteId: id } });
    },
  };
}
